import { ReactNode } from "react";
import { InfoTooltip } from "./InfoTooltip";

export interface KPICardProps {
  id: string;
  title: string;
  tooltip?: string;
  value: ReactNode;
  valueUnit?: string;
  tone: "red" | "green" | "gray";
  badge: "off" | "on" | "na";
  compare?: {
    prevLabel: string;
    wk5: { value: string; up?: boolean };
    avg: string;
  };
  subRows?: Array<{ label: string; value: string; variant?: "tgt" | "up" | "dn" }>;
  /** TODO: sparkline data from backend - not yet available */
  sparkline?: number[];
  /** Optional labels for weekly trend (panel) and x-axis semantics */
  sparklineLabels?: string[];
  /** Numeric target value for the sparkline baseline (dashed line) */
  targetValue?: number;
  /** When set, card is clickable and opens the KPI detail panel */
  onClick?: () => void;
}

const BADGE_LABELS: Record<"off" | "on" | "na", string> = {
  off: "Off Track",
  on: "On Track",
  na: "—",
};

/**
 * Inline SVG sparkline. viewBox 0 0 120 26, polyline + dashed baseline.
 * When sparkline data is undefined, renders a flat placeholder line.
 * Stroke color follows tone (red/green/gray) via CSS var(--red), var(--green), var(--t4).
 * The dashed line represents the target value; sparkline is scaled relative to it.
 */
function SparklineSVG({
  tone,
  points,
  target,
}: {
  tone: "red" | "green" | "gray";
  points?: number[];
  target?: number;
}) {
  const strokeColor =
    tone === "red" ? "var(--red)" : tone === "green" ? "var(--green)" : "var(--t4)";

  const width = 120;
  const height = 26;
  const pad = 2;

  let pointsStr: string;
  let targetY = height / 2; // Default target line position (middle)

  if (points && points.length >= 2) {
    // Include target in min/max calculation so the line is properly positioned
    const dataMin = Math.min(...points);
    const dataMax = Math.max(...points);
    const min = target != null ? Math.min(dataMin, target) : dataMin;
    const max = target != null ? Math.max(dataMax, target) : dataMax;
    const range = max - min || 1;
    const yScale = (height - 2 * pad) / range;

    // Calculate target line Y position
    if (target != null) {
      targetY = height - pad - (target - min) * yScale;
    }

    pointsStr = points
      .map((v, i) => {
        const x = (i / (points.length - 1)) * width;
        const y = height - pad - (v - min) * yScale;
        return `${x},${y}`;
      })
      .join(" ");
  } else {
    // Placeholder: flat line at bottom (like mock "0,24 120,24")
    pointsStr = `0,${height - 2} ${width},${height - 2}`;
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <polyline
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        points={pointsStr}
      />
      <line
        x1="0"
        y1={targetY}
        x2={width}
        y2={targetY}
        stroke="var(--t4)"
        strokeWidth="0.5"
        strokeDasharray="3,3"
      />
    </svg>
  );
}

function handleCardKeyDown(e: React.KeyboardEvent, onClick: (() => void) | undefined) {
  if (!onClick) return;
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    onClick();
  }
}

export const KPICard = ({
  title,
  tooltip,
  value,
  valueUnit,
  tone,
  badge,
  compare,
  subRows,
  sparkline,
  targetValue,
  onClick,
}: KPICardProps) => (
  <div
    className={`kpi ${tone}${onClick ? " kpi--clickable" : ""}`}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => handleCardKeyDown(e, onClick) : undefined}
  >
    <div className="kpi-top">
      <span className="kpi-nm-wrap">
        <span className="kpi-nm">{title}</span>
        {tooltip && <InfoTooltip text={tooltip} />}
      </span>
      <span className={`kpi-badge ${badge}`}>{BADGE_LABELS[badge]}</span>
    </div>
    <div className="kpi-val mono">
      {value}
      {valueUnit != null && <span className="u">{valueUnit}</span>}
    </div>
    {compare && (
      <div className="kpi-inline-compare">
        <span className="f-chip">
        {compare.prevLabel} <b>{compare.wk5.value}</b>
          {compare.wk5.up && " ↑"}
        </span>
        <span className="f-chip">
          Avg <b>{compare.avg}</b>
        </span>
      </div>
    )}
    <div className="kpi-spark">
      <SparklineSVG tone={tone} points={sparkline} target={targetValue} />
    </div>
    {subRows && subRows.length > 0 && (
      <div className="kpi-foot">
        {subRows.map((row, i) => (
          <div key={i} className="kpi-r">
            <span className="l">{row.label}</span>
            <span className={`v mono ${row.variant ? row.variant : ""}`.trim()}>
              {row.value}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);
